import { useState, type FormEvent } from "react";
import { useListCohorts } from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "wouter";
import { Plus, Users, Building2, Calendar, ChevronRight, Layers, Sparkles, Wand2, Copy, Check } from "lucide-react";
import InfoTip from "@/components/info-tip";
import { useToast } from "@/hooks/use-toast";

const statusStyles: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-800",
  completed: "bg-gray-100 text-gray-600",
  paused: "bg-amber-100 text-amber-800",
  draft: "bg-blue-100 text-blue-800",
};

const programLabels: Record<string, string> = {
  acceleration: "Acceleration",
  venture_building: "Venture Building",
  incubation: "Incubation",
};

// Inline founder-portal URL with a copy button so PC can grab the
// per-cohort login URL straight from the cohort card. Stops the long-tail
// of "what is the link for the May Deal founders" Slack pings dead.
function FounderUrlRow({ slug }: { slug: string }) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/${slug}/founder/login`;
  const onCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      toast({ title: "Copied founder URL", description: url });
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div
      className="mt-3 flex items-center gap-2 text-[11px] font-mono text-muted-foreground bg-muted/40 border border-border rounded px-2 py-1.5"
      onClick={(e) => e.stopPropagation()}
    >
      <span className="truncate flex-1" title={url} data-testid={`founder-url-${slug}`}>{url}</span>
      <button
        type="button"
        onClick={onCopy}
        className="flex-shrink-0 p-1 rounded hover:bg-background text-muted-foreground hover:text-foreground"
        title="Copy founder URL"
        data-testid={`copy-founder-url-${slug}`}
      >
        {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
      </button>
    </div>
  );
}

export default function AdminHome() {
  const { data: cohorts, isLoading, refetch } = useListCohorts();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { toast } = useToast();

  const [parsingAll, setParsingAll] = useState(false);

  // One-shot bulk-parse for production. Loops every company that has thesis
  // prose but no structured form, runs Favcy Navigator, and writes results.
  // Idempotent — safe to click again; only un-parsed companies are touched.
  const handleParseAllTheses = async () => {
    if (!confirm("Run Favcy Navigator on every company with a raw thesis? This will take a few seconds per company.")) return;
    setParsingAll(true);
    try {
      const res = await fetch("/api/admin/companies/parse-all-theses", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-admin-token": localStorage.getItem("admin_token") || "",
        },
        body: JSON.stringify({ force: false }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      toast({
        title: data.candidatesProcessed === 0
          ? "Already up to date"
          : `Parsed ${data.succeeded}/${data.candidatesProcessed} companies`,
        description: data.failed > 0
          ? `${data.failed} failed — check Health for details. Succeeded ones now render with bullets.`
          : `Refresh any portfolio page to see the new bullet layout.`,
      });
    } catch (err: any) {
      toast({ title: "Bulk parse failed", description: err.message, variant: "destructive" });
    } finally {
      setParsingAll(false);
    }
  };

  const handleCreate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const name = form.get("name") as string;
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/cohorts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          slug,
          description: form.get("description") || null,
          programType: form.get("programType") || "acceleration",
          startDate: form.get("startDate") ? new Date(form.get("startDate") as string).toISOString() : null,
          endDate: form.get("endDate") ? new Date(form.get("endDate") as string).toISOString() : null,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to create cohort");
      }
      toast({ title: "Cohort created", description: `${name} is ready to go.` });
      setDialogOpen(false);
      refetch();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-8" data-testid="page-admin-home">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-serif font-bold tracking-tight">Cohort Dashboard</h1>
            <InfoTip content="This is your master control panel. Each cohort is a separate program (acceleration batch, venture building engagement, etc.) with its own companies, team, founders, and investors. Create a cohort, assign your team, then click in to manage it." />
          </div>
          <p className="text-sm text-muted-foreground mt-1">Manage all cohorts across Favcy programs</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleParseAllTheses}
            disabled={parsingAll}
            title="Convert every company's wall-of-text Investment Thesis into structured bullets"
          >
            <Wand2 className="w-4 h-4 mr-2" />
            {parsingAll ? "Parsing…" : "Structure all theses"}
          </Button>
          <Link href="/admin/team">
            <Button variant="outline"><Users className="w-4 h-4 mr-2" />Team</Button>
          </Link>
          <Link href="/admin/bankers">
            <Button variant="outline" className="border-[#FF5C00]/40 text-[#FF5C00] hover:bg-[#FF5C00]/10 hover:text-[#FF5C00]"><Users className="w-4 h-4 mr-2" />Bankers</Button>
          </Link>
          <Link href="/admin/investors">
            <Button variant="outline"><Users className="w-4 h-4 mr-2" />Investors</Button>
          </Link>
          <Link href="/admin/mentors">
            <Button variant="outline" className="border-[#FF5C00]/40 text-[#FF5C00] hover:bg-[#FF5C00]/10 hover:text-[#FF5C00]"><Users className="w-4 h-4 mr-2" />Mentors</Button>
          </Link>
          <Link href="/admin/prds">
            <Button variant="outline" className="border-purple-500/40 text-purple-300 hover:bg-purple-500/10 hover:text-purple-200"><Sparkles className="w-4 h-4 mr-2" />PC · PRDs</Button>
          </Link>
          <Link href="/admin/viren">
            <Button variant="outline" className="border-purple-500/40 text-purple-300 hover:bg-purple-500/10 hover:text-purple-200"><Sparkles className="w-4 h-4 mr-2" />Viren · Banking Lead</Button>
          </Link>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="button-create-cohort"><Plus className="w-4 h-4 mr-2" />New Cohort</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-serif">Create New Cohort</DialogTitle></DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label htmlFor="name">Cohort Name</Label>
                  <Input id="name" name="name" placeholder="e.g. Cohort 7 — Healthcare" required />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea id="description" name="description" placeholder="What is this cohort focused on?" rows={2} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="programType">Program Type</Label>
                    <Select name="programType" defaultValue="acceleration">
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="acceleration">Acceleration</SelectItem>
                        <SelectItem value="venture_building">Venture Building</SelectItem>
                        <SelectItem value="incubation">Incubation</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="startDate">Start Date</Label>
                    <Input id="startDate" name="startDate" type="date" />
                  </div>
                  <div>
                    <Label htmlFor="endDate">End Date</Label>
                    <Input id="endDate" name="endDate" type="date" />
                  </div>
                </div>
                <Button type="submit" className="w-full">Create Cohort</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-48" />)}
        </div>
      ) : cohorts && cohorts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {cohorts.map((cohort: any) => (
            <Link key={cohort.id} href={`/c/${cohort.slug}`}>
              <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-primary group" data-testid={`cohort-card-${cohort.slug}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Layers className="w-4 h-4 text-primary" />
                        <Badge className={`text-xs ${statusStyles[cohort.status] || ""}`}>{cohort.status}</Badge>
                        <Badge variant="outline" className="text-xs">{programLabels[cohort.programType] || cohort.programType}</Badge>
                        <code className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{cohort.slug}</code>
                      </div>
                      <h3 className="text-lg font-serif font-bold truncate">{cohort.name}</h3>
                      {cohort.description && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{cohort.description}</p>
                      )}
                      <FounderUrlRow slug={cohort.slug} />
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground/40 group-hover:text-primary transition-colors mt-1 flex-shrink-0" />
                  </div>

                  <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border">
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Building2 className="w-3.5 h-3.5" />
                      <span className="font-medium">{cohort.companyCount}</span> companies
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Users className="w-3.5 h-3.5" />
                      <span className="font-medium">{cohort.teamMembers?.length || 0}</span> team
                    </div>
                    {cohort.startDate && (
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(cohort.startDate).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                        {cohort.endDate && ` — ${new Date(cohort.endDate).toLocaleDateString("en-US", { month: "short", year: "numeric" })}`}
                      </div>
                    )}
                  </div>

                  {cohort.teamMembers?.length > 0 && (
                    <div className="flex items-center gap-1 mt-3">
                      {cohort.teamMembers.map((m: any) => (
                        <div key={m.id} className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-xs font-medium" title={`${m.name} (${m.role})`}>
                          {m.name.split(" ").map((n: string) => n[0]).join("")}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Layers className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-lg font-serif font-bold mb-1">No cohorts yet</h3>
            <p className="text-sm text-muted-foreground mb-4">Create your first cohort to start managing companies.</p>
            <Button onClick={() => setDialogOpen(true)}><Plus className="w-4 h-4 mr-2" />Create First Cohort</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
